import Button from "@ui/button/Button"
import { EntityInput } from "./EntityInput"
import { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { toast } from "react-toastify"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { T } from "../adminTheme"
import { date, Disclosure, Fields, FilterBar, LinkButton, Muted, Page, Pager, Panel, Row, State, StatusBadge, Table } from "./adminUi"
import { UserOverviewData } from "./UserOverviewData"
import { formatAdminRoute } from "src/utils/adminRoute"

/** "card.transaction_declined" → "Card transaction declined"; the API value is never changed. */
export const eventLabel = (type: string) => {
	if (type === "crypto.gas_treasury_unreadable") return "Gas: balance unreadable"
	const text = type.replace(/[._]+/g, " ").trim().replace(/\b(kyc|trx|api|otp|uid)\b/gi, (word) => word.toUpperCase())
	return text ? text[0].toUpperCase() + text.slice(1) : type
}

const PAGE_SIZE = 50

interface IEventTypesResponse {
	known: string[]
	actions?: string[]
	levels: string[]
	results?: string[]
	journaled: Array<{ action?: string; type: string; level: string; count: number; last_at: string }>
}

interface ISystemEvent {
	id: number
	uid: string
	level: string
	type: string
	action?: string
	result?: string | null
	owner_type?: string | null
	owner_uid?: string | null
	source?: string
	message: string
	entity_type?: string
	entity_uid?: string
	related_uid?: string
	user_uid?: string
	company_uid?: string
	actor_uid?: string
	actor_email?: string
	data: unknown
	created_at: string
}

interface IEventListResponse {
	total: number
	limit: number
	offset: number
	rows: ISystemEvent[]
}

const eventData = (value: unknown): Record<string, unknown> | null =>
	value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null

/** company.topup amounts are cents; renamed to *_cents so the data view shows them as dollars. */
const CENTS = ["amount", "fee_amount", "net_amount"]
const centsKeys = (data: Record<string, unknown>): Record<string, unknown> => Object.fromEntries(Object.entries(data).map(([key, item]) =>
	CENTS.includes(key) && typeof item === "number" ? [`${key}_cents`, item]
		: key === "fee" && item && typeof item === "object" ? [key, centsKeys(item as Record<string, unknown>)] : [key, item]))

const visibleData = (value: unknown, action?: string) => {
	const data = eventData(value)
	if (data && action === "company.topup") return centsKeys(data)
	if (!data || !("raw" in data)) return value
	return Object.fromEntries(Object.entries(data).filter(([key]) => key !== "raw"))
}

const sentence = (text: string) => { const spaced = text.replaceAll("_", " "); return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : text }

/** Tiny inline toggle for table cells, where a full card would be too heavy. */
const InlineToggle = styled.details`
	summary { list-style: none; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; margin-left: -4px; padding: 3px 8px 3px 4px; border-radius: 6px; font-size: 13px; font-weight: 550; color: ${T.muted}; user-select: none; transition: background-color .14s ${T.ease}, color .14s ${T.ease}; }
	summary::-webkit-details-marker { display: none; }
	summary::before { content: "›"; display: inline-block; font-size: 17px; line-height: 1; transition: transform .2s ${T.ease}; }
	summary:hover { background: ${T.hover}; color: ${T.text}; }
	&[open] > summary::before { transform: rotate(90deg); }
	&[open] > div { animation: bo-rise .24s ${T.ease} both; }
`

/** One "label: value" line in the Related column. */
const Line = ({ label, children }: { label: string; children: React.ReactNode }) => <small><Muted>{label}: </Muted>{children}</small>

type EventFilters = { level: string; action: string; result: string; user: string; user_uid: string; actor: string; company_uid: string; card_uid: string; entity_type: string; entity_uid: string; actor_email: string; from: string; to: string; search: string }
const emptyFilters: EventFilters = { level: "", action: "", result: "", user: "", user_uid: "", actor: "", company_uid: "", card_uid: "", entity_type: "", entity_uid: "", actor_email: "", from: "", to: "", search: "" }

export const SuperAdminEvents = ({ token, initialFilters = {}, onOpenUser, onOpenCompany }: { token: string; initialFilters?: Partial<EventFilters>; onOpenUser?: (uid: string) => void; onOpenCompany?: (uid: string) => void }) => {
	const [types, setTypes] = useState<IEventTypesResponse>({ known: [], levels: [], journaled: [] })
	const [rows, setRows] = useState<ISystemEvent[]>([])
	const [total, setTotal] = useState(0)
	const [offset, setOffset] = useState(0)
	const [loading, setLoading] = useState(false)
	const [filters, setFilters] = useState<EventFilters>({ ...emptyFilters, ...initialFilters })
	const moreKeys: Array<keyof EventFilters> = ["from", "to", "user", "actor", "company_uid", "card_uid", "user_uid", "entity_type", "entity_uid", "actor_email"]
	const [initiallyOpen] = useState(
		moreKeys.some((key) => initialFilters[key]) || [initialFilters.level, initialFilters.action, initialFilters.result].some((value) => value?.includes(","))
	)
	const requestId = useRef(0)

	const loadEvents = useCallback(async (nextOffset: number, nextFilters: EventFilters = filters) => {
		const id = ++requestId.current
		setLoading(true)
		const response = await systemPost<IEventListResponse>(token, "system/events/list", {
			...nextFilters,
			limit: PAGE_SIZE,
			offset: nextOffset
		})
		if (id !== requestId.current) return
		setLoading(false)
		if (response.error) return toast.error(systemErrorMessage(response.error))
		setRows(response.data.rows ?? [])
		setTotal(Number(response.data.total ?? 0))
		setOffset(Number(response.data.offset ?? nextOffset))
	}, [filters, token])

	useEffect(() => {
		const load = async () => {
			const response = await systemPost<IEventTypesResponse>(token, "system/events/types")
			if (response.error) toast.error(systemErrorMessage(response.error))
			else setTypes(response.data)
		}
		void load()
	}, [token])

	useEffect(() => { void loadEvents(0); return () => { requestId.current++ } }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

	const setFilter = (name: keyof typeof filters, value: string) => setFilters((current) => ({ ...current, [name]: value }))
	const actionOptions = [...new Set([...(types.actions ?? types.known ?? []), ...(types.journaled ?? []).map((item) => item.action ?? item.type)])]
	const moreActive = moreKeys.some((key) => filters[key]) || [filters.level, filters.action, filters.result].some((value) => value.includes(","))
	const text = (name: keyof EventFilters, label: string, placeholder?: string, grow = false) =>
		<label style={grow ? { flex: "1 1 220px" } : undefined}>{label}<input name={name} value={filters[name]} placeholder={placeholder} onChange={(event) => setFilter(name, event.target.value)} /></label>
	const pick = (name: "level" | "action" | "result", label: string, all: string, options: Array<[string, string]>) =>
		<label>{label}<select name={name} value={filters[name]} onChange={(event) => setFilter(name, event.target.value)}><option value=''>{all}</option>{options.map(([id, text]) => <option key={id} value={id}>{text}</option>)}</select></label>
	const moment = (name: "from" | "to", label: string) =>
		<label>{label}<input type='datetime-local' style={{ minWidth: 200 }} value={filters[name].replace(" ", "T")} onChange={(event) => setFilter(name, event.target.value.replace("T", " "))} /></label>

	return <Page>
		<Panel title='Filters' hint='Narrow the journal by what happened, to whom and when.'>
			<FilterBar>
				{pick("level", "Level", "All levels", types.levels.map((level) => [level, sentence(level)]))}
				{pick("action", "Action", "All actions", actionOptions.map((action) => [action, eventLabel(action)]))}
				{pick("result", "Result", "All results", (types.results ?? []).map((result) => [result, sentence(result)]))}
				{text("search", "Search", "Message or JSON data", true)}
				<Row style={{ marginLeft: "auto" }}>
					<Button variant='secondarySmall' onClick={() => { setFilters(emptyFilters); void loadEvents(0, emptyFilters) }}>Reset</Button>
					<Button variant='primarySmall' onClick={() => void loadEvents(0)} disabled={loading}>Apply filters</Button>
				</Row>
			</FilterBar>
			<div style={{ marginTop: 14 }}>
				<Disclosure title={`More filters${moreActive ? " · active" : ""}`} defaultOpen={initiallyOpen}>
					<Fields>
						{moment("from", "From")}
						{moment("to", "To")}
						<label>User<EntityInput token={token} kind='user' name='user' value={filters.user} onChange={(value) => setFilter("user", value)} /></label>
						<label>Who did it<EntityInput token={token} kind='user' name='actor' value={filters.actor} placeholder='Name, e-mail or UID (admins too)' onChange={(value) => setFilter("actor", value)} /></label>
						<label>Company<EntityInput token={token} kind='company' name='company_uid' value={filters.company_uid} onChange={(value) => setFilter("company_uid", value)} /></label>
						<label>Card<EntityInput token={token} kind='card' name='card_uid' value={filters.card_uid} onChange={(value) => setFilter("card_uid", value)} /></label>
						{text("level", "Levels (comma separated)")}
						{text("action", "Actions (comma separated)")}
						{text("result", "Results (comma separated)")}
						<label>Subject user<EntityInput token={token} kind='user' name='user_uid' value={filters.user_uid} onChange={(value) => setFilter("user_uid", value)} /></label>
						{text("entity_type", "Entity type")}
						{text("entity_uid", "Entity UID")}
						{text("actor_email", "Exact actor e-mail")}
					</Fields>
				</Disclosure>
			</div>
		</Panel>

		<Panel title='Event journal' hint={loading ? undefined : `${rows.length} of ${total} events`}>
			<State loading={loading} empty={!loading && rows.length === 0} emptyText='The journal is empty for these filters.' />
			{rows.length > 0 && <Table>
				<thead><tr><th>When</th><th>Level and result</th><th>Event</th><th>Related</th></tr></thead>
				<tbody>{rows.map((event) => {
					const action = event.action ?? event.type
					const data = eventData(event.data)
					return <tr key={event.uid || event.id} style={{ verticalAlign: "top" }}>
						<td style={{ whiteSpace: "nowrap" }}>{date(event.created_at)}</td>
						<td><Row style={{ gap: 6 }}><StatusBadge value={event.level} />{event.result && <StatusBadge value={event.result} />}</Row></td>
						<td>
							<span title={action} style={{ fontWeight: 600 }}>{eventLabel(action)}</span>
							<small>{event.message}</small>
							{action === "card.transaction_declined" && typeof data?.decline_reason === "string" && <small style={{ color: T.danger, fontWeight: 550 }}>{`Decline reason: ${sentence(data.decline_reason)}`}</small>}
							{action === "card.transaction_sync_failed" && typeof data?.reason === "string" && <small style={{ color: T.danger, fontWeight: 550 }}>{`Sync failure: ${data.reason}`}</small>}
							<InlineToggle style={{ marginTop: 8 }}><summary>Data</summary><div style={{ marginTop: 8 }}><UserOverviewData value={visibleData(event.data, action)} /></div></InlineToggle>
							{data?.raw !== undefined && data.raw !== null && <InlineToggle style={{ marginTop: 4 }}><summary>Issuer raw response</summary><div style={{ marginTop: 8 }}><UserOverviewData value={data.raw} /></div></InlineToggle>}
						</td>
						<td>
							<Line label={eventLabel(event.entity_type || "event")}>{event.entity_type === "card" && event.entity_uid && event.company_uid
								? <LinkButton as='a' href={formatAdminRoute({ tab: "company", company: event.company_uid, card: event.entity_uid })}>{event.entity_uid}</LinkButton>
								: event.entity_uid || "—"}{event.related_uid ? ` ↔ ${event.related_uid}` : ""}</Line>
							<Line label='User'>{event.user_uid ? <LinkButton type='button' onClick={() => onOpenUser?.(event.user_uid!)}>{event.user_uid}</LinkButton> : "—"}</Line>
							<Line label='Company'>{event.company_uid ? <LinkButton type='button' onClick={() => onOpenCompany?.(event.company_uid!)}>{event.company_uid}</LinkButton> : "—"}</Line>
							<Line label='Who did it'>{event.actor_email || event.actor_uid || "system"}</Line>
							{event.owner_uid && <Line label={`Owner (${event.owner_type})`}><LinkButton type='button' onClick={() => event.owner_type === "user" ? onOpenUser?.(event.owner_uid!) : event.owner_type === "company" ? onOpenCompany?.(event.owner_uid!) : undefined}>{event.owner_uid}</LinkButton></Line>}
						</td>
					</tr>
				})}</tbody>
			</Table>}
			{total > PAGE_SIZE && <Pager total={total} from={offset + 1} to={offset + rows.length} page={Math.floor(offset / PAGE_SIZE)} pages={Math.ceil(total / PAGE_SIZE)} onPage={(next) => { if (!loading) void loadEvents(next * PAGE_SIZE) }} />}
		</Panel>
	</Page>
}
